import axios from 'axios';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { configSettings, getConfiguration } from '../../shared/configuration/config-settings';
import { UserPaymentMethod, UserPaymentMethodRequest, PaymentMethod } from '@finpay/payment-types';

// ============================================================================
// LOCAL DEVELOPMENT FLAG
// Set to true to point payment API calls to a local instance
// ============================================================================
const USE_LOCAL_PAYMENT_API = false;
const LOCAL_PAYMENT_API_URL = 'http://localhost:8085'; // Change port as needed
const LOCAL_API_PATH_PREFIX = '/qa/v3';  // Local uses /qa/v3 instead of /payments/v3
const REMOTE_API_PATH_PREFIX = '/payments/v3';
// ============================================================================

/**
 * Get the base URL for payment API calls
 * Uses local URL if USE_LOCAL_PAYMENT_API is true, otherwise uses config
 */
export const getPaymentApiBaseUrl = (): string => {
  if (USE_LOCAL_PAYMENT_API) {
    console.log('[PaymentMethodService] Using LOCAL payment API:', LOCAL_PAYMENT_API_URL);
    return LOCAL_PAYMENT_API_URL;
  }
  return configSettings.unified_aws_app_gateway_uri;
};

/**
 * Get the API path prefix based on whether we're using local or remote API
 * Local uses /qa/v3, remote uses /payments/v3
 */
export const getPaymentApiPathPrefix = (): string => {
  return USE_LOCAL_PAYMENT_API ? LOCAL_API_PATH_PREFIX : REMOTE_API_PATH_PREFIX;
};

// Stripe singleton
const config = getConfiguration();
let stripePromise: Promise<Stripe | null> | null = null;
export const getStripeInstance = async (): Promise<Stripe | null> => {
  if (!stripePromise && config.stripe_key) {
    stripePromise = loadStripe(config.stripe_key);
  }
  return stripePromise;
};

// Re-export PaymentMethod enum for use in hub components
export { PaymentMethod };


export interface SavePaymentMethodParams extends UserPaymentMethodRequest {
  /** User ID in our system (required for the API endpoint) */
  userId: number;
  /** Auth token for API calls */
  authToken?: string | null;
}


export const savePaymentMethod = async (
  params: SavePaymentMethodParams
): Promise<UserPaymentMethod> => {
  const {
    userId,
    extPaymentMethodId,
    extPaymentUserId,
    extPaymentIntentId,
    extSetupIntentId,
    paymentMethodTypeId,
    last4,
    cardBrand,
    methodHash,
    isVerified = true,
    expMonth,
    expYear,
    authToken,
  } = params;

  const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method`;

  const requestBody: UserPaymentMethodRequest = {
    extPaymentMethodId,
    ...(extPaymentUserId && { extPaymentUserId }),
    extPaymentIntentId,
    extSetupIntentId,
    paymentMethodTypeId,
    isActive: true,
    isVerified,
    last4,
    cardBrand,
    ...(methodHash && { methodHash }),
    ...(expMonth && { expMonth }),
    ...(expYear && { expYear }),
  };

  console.log('[PaymentMethodService] Saving payment method:', {
    url,
    userId,
    paymentMethodTypeId,
    cardBrand,
    last4: `****${last4}`,
    methodHash: methodHash ? '***' : undefined,
    extPaymentIntentId,
    extSetupIntentId,
  });

  const response = await axios.post(url, requestBody, {
    headers: {
      'Content-Type': 'application/json',
      ...(authToken && {
        Authorization: `Bearer ${authToken}`,
        'x-cog-id': authToken,
      }),
    },
  });

  console.log('[PaymentMethodService] Payment method saved:', response.data);

  return response.data;
};

/**
 * Deactivate a payment method by setting isActive to false
 */
export interface DeactivatePaymentMethodParams {
  userId: number;
  userPaymentMethodId: number;
  authToken?: string | null;
}

export const deactivatePaymentMethod = async (
  params: DeactivatePaymentMethodParams
): Promise<{ success: boolean; message: string }> => {
  const { userId, userPaymentMethodId, authToken } = params;

  const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method/${userPaymentMethodId}`;

  console.log('[PaymentMethodService] Deactivating payment method:', {
    url,
    userId,
    userPaymentMethodId,
  });

  try {
    await axios.patch(
      url,
      { isActive: false },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && {
            Authorization: `Bearer ${authToken}`,
            'x-cog-id': authToken,
          }),
        },
      }
    );

    console.log('[PaymentMethodService] Payment method deactivated successfully');
    return { success: true, message: 'Payment method removed.' };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error deactivating payment method:', err);
    const responseData = err?.response?.data;
    return {
      success: false,
      message: responseData?.externalResponse?.message || responseData?.message || 'Failed to remove payment method.',
    };
  }
};

/**
 * Parameters for fetching user payment methods
 */
export interface FetchUserPaymentMethodsParams {
  userId: number;
  authToken?: string | null;
}

/**
 * Result from fetching user payment methods
 */
export interface FetchUserPaymentMethodsResult {
  success: boolean;
  message: string;
  paymentMethods: UserPaymentMethod[];
}

/**
 * Fetch all payment methods for a user
 */
export const fetchUserPaymentMethods = async (
  params: FetchUserPaymentMethodsParams
): Promise<FetchUserPaymentMethodsResult> => {
  const { userId, authToken } = params;

  try {
    console.log('[PaymentMethodService] Fetching payment methods for user:', userId);

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method`;
    
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });

    if (response.status === 200 && Array.isArray(response.data)) {
      console.log('[PaymentMethodService] Payment methods fetched:', response.data.length);
      return {
        success: true,
        message: 'Payment methods fetched successfully.',
        paymentMethods: response.data as UserPaymentMethod[],
      };
    }

    return {
      success: false,
      message: 'Unexpected response format.',
      paymentMethods: [],
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error fetching payment methods:', err);
    const responseData = err?.response?.data;
    return {
      success: false,
      message: responseData?.externalResponse?.message || responseData?.message || 'Failed to fetch payment methods.',
      paymentMethods: [],
    };
  }
};

/**
 * Parameters for creating an ACH (bank account) payment method
 */
export interface CreateACHPaymentMethodParams {
  routingNumber: string;
  accountNumber: string;
  accountHolderType?: 'individual' | 'company';
  accountType?: 'checking' | 'savings';
  billingDetails: {
    name: string;
    email: string;
  };
}

/**
 * Result from creating an ACH payment method
 */
export interface CreateACHPaymentMethodResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  fingerprint?: string;
  bankName?: string;
}

/**
 * Create an ACH (bank account) payment method using Stripe
 */
export const createACHPaymentMethod = async (
  params: CreateACHPaymentMethodParams
): Promise<CreateACHPaymentMethodResult> => {
  const {
    routingNumber,
    accountNumber,
    accountHolderType = 'individual',
    accountType = 'checking',
    billingDetails,
  } = params;

  try {
    const stripe = await getStripeInstance();
    if (!stripe) {
      return {
        success: false,
        message: 'Stripe has not been initialized.',
      };
    }

    console.log('[PaymentMethodService] Creating ACH PaymentMethod...');

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'us_bank_account',
      us_bank_account: {
        routing_number: routingNumber,
        account_number: accountNumber,
        account_holder_type: accountHolderType,
        account_type: accountType,
      },
      billing_details: {
        name: billingDetails.name,
        email: billingDetails.email,
      },
    });

    if (error) {
      console.error('[PaymentMethodService] Stripe error:', error);
      return {
        success: false,
        message: error.message || 'Failed to create payment method.',
      };
    }

    if (!paymentMethod) {
      return {
        success: false,
        message: 'Failed to create payment method.',
      };
    }

    console.log('[PaymentMethodService] ✅ ACH PaymentMethod created:', paymentMethod.id);
    console.log('[PaymentMethodService] Fingerprint:', paymentMethod.us_bank_account?.fingerprint);
    console.log('[PaymentMethodService] Bank:', paymentMethod.us_bank_account?.bank_name);

    return {
      success: true,
      message: 'Bank account verified.',
      paymentMethodId: paymentMethod.id,
      last4: paymentMethod.us_bank_account?.last4 || accountNumber.slice(-4),
      fingerprint: paymentMethod.us_bank_account?.fingerprint,
      bankName: paymentMethod.us_bank_account?.bank_name,
    };
  } catch (err) {
    console.error('[PaymentMethodService] Error creating ACH payment method:', err);
    return {
      success: false,
      message: 'An unexpected error occurred.',
    };
  }
};

/**
 * Parameters for creating a payment
 */
export interface CreatePaymentParams {
  /** Program ID for the payment */
  programId: number;
  /** Amount in dollars (e.g., 10.50 for $10.50) */
  amount: number;
  /** FinPay UserPaymentMethod ID (primary key from user_payment_method table) */
  userPaymentMethodId: number;
  /** Payment type ID (1 = standard payment) */
  paymentTypeId?: number;
  /** Payment channel ID (1 = default channel) */
  paymentChannelId?: number;
}

/**
 * Result from creating a payment
 */
export interface CreatePaymentResult {
  success: boolean;
  message: string;
  paymentIntentId?: string;
  clientSecret?: string;
  status?: string;
}

/**
 * Create a payment using the backend API
 * Uses isAuthOnly: false to capture payment immediately
 */
export const createPayment = async (
  params: CreatePaymentParams
): Promise<CreatePaymentResult> => {
  const { programId, amount, userPaymentMethodId, paymentTypeId = 1, paymentChannelId = 1 } = params;

  try {
    console.log('[PaymentMethodService] Creating Payment...');
    console.log('[PaymentMethodService] ProgramId:', programId, 'Amount:', amount, 'UserPaymentMethodId:', userPaymentMethodId);

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/program/${programId}/payment`;
    
    const response = await axios.post(url, {
      paymentAmt: amount,
      userPaymentMethodId,
      isAuthOnly: false,
      paymentTypeId,
      paymentChannelId,
    });

    const entity = response.data;
    if (!entity) {
      console.error('[PaymentMethodService] Error creating Payment: No response data');
      return {
        success: false,
        message: 'Failed to create payment.',
      };
    }

    // Check for paymentId (FinPay response) or Stripe-style IDs
    const paymentId = entity.paymentId || entity.id || entity.paymentIntentId || entity.extPaymentIntentId || entity.client_secret?.split('_secret_')[0];
    
    console.log('[PaymentMethodService] ✅ Payment created:', paymentId);
    console.log('[PaymentMethodService] Status:', entity.status || entity.paymentStatus);
    console.log('[PaymentMethodService] Full response entity:', entity);

    if (!paymentId) {
      console.error('[PaymentMethodService] Payment created but no payment ID returned');
      return {
        success: false,
        message: 'Payment created but no payment ID returned.',
      };
    }

    const paymentStatus = (entity.status || entity.paymentStatus || '').toLowerCase();
    const isProcessing = paymentStatus === 'processing';
    const successMessage = isProcessing 
      ? 'Payment submitted, please check back later for status.'
      : 'Payment successful';

    return {
      success: true,
      message: successMessage,
      paymentIntentId: String(paymentId),
      clientSecret: entity.client_secret,
      status: entity.status || entity.paymentStatus,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error creating Payment:', err);
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating payment.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

/**
 * Update a PaymentIntent (for amount or customer changes)
 */
export interface UpdatePaymentIntentParams {
  paymentIntentId: string;
  amount?: number;
  extPaymentUserId?: string;
}

export interface UpdatePaymentIntentResult {
  success: boolean;
  message: string;
  clientSecret?: string;
}

export const updatePaymentIntent = async (
  params: UpdatePaymentIntentParams
): Promise<UpdatePaymentIntentResult> => {
  const { paymentIntentId, amount, extPaymentUserId } = params;

  try {
    console.log('[PaymentMethodService] Updating PaymentIntent:', paymentIntentId);

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/payment-intent/${paymentIntentId}`;
    
    const response = await axios.patch(url, {
      ...(amount !== undefined && { amount: Math.round(amount * 100) }),
      ...(extPaymentUserId && { customer: extPaymentUserId }),
    });

    const entity = response.data;
    if (!entity) {
      console.error('[PaymentMethodService] Error updating PaymentIntent: No response data');
      return {
        success: false,
        message: 'Failed to update payment intent.',
      };
    }

    console.log('[PaymentMethodService] ✅ PaymentIntent updated');

    return {
      success: true,
      message: 'Payment intent updated.',
      clientSecret: entity.client_secret,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error updating PaymentIntent:', err);
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred updating payment intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

// ============================================================================
// SETUP INTENT APIs (for saving payment methods with verification)
// Works for both Card and ACH. ACH uses instant verification via Financial Connections.
// ============================================================================

/**
 * Parameters for creating a SetupIntent
 */
export interface CreateSetupIntentParams {
  /** Stripe Customer ID (optional - can be added later via updateSetupIntent) */
  extPaymentUserId?: string;
  /** Payment method type ID: 1 = Card, 2 = BankAccount (optional) */
  paymentMethodTypeId?: 1 | 2;
}

/**
 * Result from creating a SetupIntent
 */
export interface CreateSetupIntentResult {
  success: boolean;
  message: string;
  setupIntentId?: string;
  clientSecret?: string;
  status?: string;
}

/**
 * Create a SetupIntent for saving a payment method
 * 
 * Frontend Flow:
 * 1. Call this function to get a SetupIntent with client_secret
 * 2. For ACH: Use stripe.collectBankAccountForSetup() with the client_secret
 * 3. For Card: Use stripe.confirmCardSetup() with the client_secret
 * 4. Payment method is verified and attached to customer
 * 5. Save the payment_method ID via savePaymentMethod()
 */
export const createSetupIntent = async (
  params: CreateSetupIntentParams = {}
): Promise<CreateSetupIntentResult> => {
  const { extPaymentUserId, paymentMethodTypeId } = params;

  try {
    console.log('[PaymentMethodService] Creating SetupIntent...');
    console.log('[PaymentMethodService] Customer:', extPaymentUserId || '(none - will be added later)', 'PaymentMethodTypeId:', paymentMethodTypeId || 'default');

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/setup-intent`;
    
    const requestBody: { 
      extPaymentUserId?: string; 
      paymentMethodTypeId?: number;
    } = {};
    
    // Only include extPaymentUserId if provided
    if (extPaymentUserId) {
      requestBody.extPaymentUserId = extPaymentUserId;
    }
    
    // Include paymentMethodTypeId if provided (1 = Card, 2 = BankAccount)
    if (paymentMethodTypeId) {
      requestBody.paymentMethodTypeId = paymentMethodTypeId;
    }

    const response = await axios.post(url, requestBody);

    const entity = response.data;
    if (!entity) {
      console.error('[PaymentMethodService] Error creating SetupIntent: No response data');
      return {
        success: false,
        message: 'Failed to create setup intent.',
      };
    }

    const setupIntentId = entity.id || entity.client_secret?.split('_secret_')[0];
    
    console.log('[PaymentMethodService] ✅ SetupIntent created:', setupIntentId);
    console.log('[PaymentMethodService] Status:', entity.status);

    return {
      success: true,
      message: 'Setup intent created.',
      setupIntentId,
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error creating SetupIntent:', err);
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating setup intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

/**
 * Parameters for confirming a SetupIntent with manual bank account entry
 */
export interface ConfirmACHSetupIntentParams {
  /** SetupIntent client secret */
  clientSecret: string;
  /** Bank routing number */
  routingNumber: string;
  /** Bank account number */
  accountNumber: string;
  /** Account holder type */
  accountHolderType: 'individual' | 'company';
  /** Account type */
  accountType: 'checking' | 'savings';
  /** Billing details */
  billingDetails: {
    name: string;
    email: string;
  };
}

/**
 * Result from confirming a SetupIntent
 */
export interface ConfirmACHSetupIntentResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  bankName?: string;
  fingerprint?: string;
  status?: string;
}

/**
 * Confirm a SetupIntent with manually entered bank account details
 * 
 * This function:
 * 1. Creates a PaymentMethod from the bank details
 * 2. Confirms the SetupIntent with the PaymentMethod
 * 3. The PaymentMethod is attached to the customer
 * 
 * Use this for 3rd party entry where the operator enters customer's bank details.
 */
export const confirmACHSetupIntent = async (
  params: ConfirmACHSetupIntentParams
): Promise<ConfirmACHSetupIntentResult> => {
  const {
    clientSecret,
    routingNumber,
    accountNumber,
    accountHolderType,
    accountType,
    billingDetails,
  } = params;

  try {
    const stripe = await getStripeInstance();
    if (!stripe) {
      return {
        success: false,
        message: 'Stripe has not been initialized.',
      };
    }

    console.log('[PaymentMethodService] Confirming ACH SetupIntent with manual bank details...');

    // Confirm the SetupIntent with bank account details
    // This creates the PaymentMethod and attaches it to the customer in one step
    const result = await stripe.confirmUsBankAccountSetup(clientSecret, {
      payment_method: {
        us_bank_account: {
          routing_number: routingNumber,
          account_number: accountNumber,
          account_holder_type: accountHolderType,
          account_type: accountType,
        },
        billing_details: {
          name: billingDetails.name,
          email: billingDetails.email,
        },
      },
    });

    if (result.error) {
      console.error('[PaymentMethodService] SetupIntent confirmation error:', result.error);
      return {
        success: false,
        message: result.error.message || 'Failed to confirm bank account setup.',
      };
    }

    const setupIntent = result.setupIntent;
    if (!setupIntent) {
      return {
        success: false,
        message: 'No setup intent returned.',
      };
    }

    console.log('[PaymentMethodService] SetupIntent status:', setupIntent.status);

    // Extract payment method ID
    const paymentMethodId = typeof setupIntent.payment_method === 'string' 
      ? setupIntent.payment_method 
      : setupIntent.payment_method?.id;

    if (!paymentMethodId) {
      return {
        success: false,
        message: 'No payment method ID returned.',
      };
    }

    // Extract bank account details if available
    const usBankAccount = typeof setupIntent.payment_method !== 'string' 
      ? setupIntent.payment_method?.us_bank_account 
      : undefined;

    console.log('[PaymentMethodService] ✅ ACH SetupIntent confirmed:', paymentMethodId);
    console.log('[PaymentMethodService] Status:', setupIntent.status);

    return {
      success: true,
      message: 'Bank account setup confirmed.',
      paymentMethodId,
      last4: usBankAccount?.last4 || accountNumber.slice(-4),
      bankName: usBankAccount?.bank_name,
      fingerprint: usBankAccount?.fingerprint,
      status: setupIntent.status,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error confirming ACH SetupIntent:', err);
    return {
      success: false,
      message: err.message || 'An unexpected error occurred.',
    };
  }
};

// ============================================================================
// UPDATE SETUP INTENT - Attach customer to existing SetupIntent
// ============================================================================

/**
 * Parameters for updating a SetupIntent
 */
export interface UpdateSetupIntentParams {
  /** SetupIntent ID to update */
  setupIntentId: string;
  /** Stripe Customer ID to attach (optional) */
  extPaymentUserId?: string;
  /** Payment method types to enable (e.g., ['card'] or ['card', 'us_bank_account']) */
  paymentMethodTypes?: string[];
}

/**
 * Result from updating a SetupIntent
 */
export interface UpdateSetupIntentResult {
  success: boolean;
  message: string;
  clientSecret?: string;
  status?: string;
}

/**
 * Update a SetupIntent to attach a customer or change payment method types
 * 
 * This must be called BEFORE confirming the SetupIntent.
 * Once the SetupIntent is confirmed, the customer cannot be changed.
 */
export const updateSetupIntent = async (
  params: UpdateSetupIntentParams
): Promise<UpdateSetupIntentResult> => {
  const { setupIntentId, extPaymentUserId, paymentMethodTypes } = params;

  try {
    console.log('[PaymentMethodService] Updating SetupIntent...');
    console.log('[PaymentMethodService] SetupIntent:', setupIntentId, 'Customer:', extPaymentUserId || '(unchanged)', 'PaymentMethodTypes:', paymentMethodTypes || '(unchanged)');

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/setup-intent/${setupIntentId}`;
    console.log('[PaymentMethodService] PATCH URL:', url);
    
    const requestBody: {
      extPaymentUserId?: string;
      paymentMethodTypes?: string[];
    } = {};

    if (extPaymentUserId) {
      requestBody.extPaymentUserId = extPaymentUserId;
    }

    if (paymentMethodTypes && paymentMethodTypes.length > 0) {
      requestBody.paymentMethodTypes = paymentMethodTypes;
    }

    const response = await axios({
      method: 'PATCH',
      url,
      data: requestBody,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const entity = response.data;
    if (!entity) {
      console.error('[PaymentMethodService] Error updating SetupIntent: No response data');
      return {
        success: false,
        message: 'Failed to update setup intent.',
      };
    }

    console.log('[PaymentMethodService] ✅ SetupIntent updated with customer:', extPaymentUserId);
    console.log('[PaymentMethodService] Status:', entity.status);

    return {
      success: true,
      message: 'Setup intent updated.',
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error updating SetupIntent:', err);
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred updating setup intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

// ============================================================================
// CONFIRM CARD SETUP INTENT - Confirm SetupIntent with card details
// ============================================================================

/**
 * Parameters for confirming a card SetupIntent
 */
export interface ConfirmCardSetupIntentParams {
  /** SetupIntent client secret */
  clientSecret: string;
  /** Stripe CardElement or card details */
  cardElement: any;
  /** Billing details */
  billingDetails: {
    name: string;
    email: string;
  };
}

/**
 * Result from confirming a card SetupIntent
 */
export interface ConfirmCardSetupIntentResult {
  success: boolean;
  message: string;
  paymentMethodId?: string;
  last4?: string;
  brand?: string;
  expMonth?: number;
  expYear?: number;
  fingerprint?: string;
  status?: string;
}

/**
 * Confirm a SetupIntent with card details
 * 
 * This function confirms the SetupIntent which:
 * 1. Creates the PaymentMethod
 * 2. Attaches it to the customer (if customer was set on SetupIntent)
 * 3. Returns the confirmed SetupIntent with payment method details
 */
export const confirmCardSetupIntent = async (
  params: ConfirmCardSetupIntentParams
): Promise<ConfirmCardSetupIntentResult> => {
  const { clientSecret, cardElement, billingDetails } = params;

  try {
    const stripe = await getStripeInstance();
    if (!stripe) {
      return {
        success: false,
        message: 'Stripe has not been initialized.',
      };
    }

    console.log('[PaymentMethodService] Confirming Card SetupIntent...');

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: billingDetails.name,
          email: billingDetails.email,
        },
      },
    });

    if (result.error) {
      console.error('[PaymentMethodService] SetupIntent confirmation error:', result.error);
      return {
        success: false,
        message: result.error.message || 'Failed to confirm card setup.',
      };
    }

    const setupIntent = result.setupIntent;
    if (!setupIntent) {
      return {
        success: false,
        message: 'No setup intent returned.',
      };
    }

    console.log('[PaymentMethodService] SetupIntent status:', setupIntent.status);

    // Extract payment method ID
    const paymentMethodId = typeof setupIntent.payment_method === 'string' 
      ? setupIntent.payment_method 
      : setupIntent.payment_method?.id;

    if (!paymentMethodId) {
      return {
        success: false,
        message: 'No payment method ID returned.',
      };
    }

    // Extract card details if available
    const card = typeof setupIntent.payment_method !== 'string' 
      ? setupIntent.payment_method?.card 
      : undefined;

    console.log('[PaymentMethodService] ✅ Card SetupIntent confirmed:', paymentMethodId);
    console.log('[PaymentMethodService] Status:', setupIntent.status);

    return {
      success: true,
      message: 'Card setup confirmed.',
      paymentMethodId,
      last4: card?.last4,
      brand: card?.brand,
      expMonth: card?.exp_month,
      expYear: card?.exp_year,
      fingerprint: card?.fingerprint ?? undefined,
      status: setupIntent.status,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error confirming Card SetupIntent:', err);
    return {
      success: false,
      message: err.message || 'An unexpected error occurred.',
    };
  }
};

// ============================================================================
// SAVE PAYMENT METHOD FROM STRIPE - Backend fetches details and saves
// ============================================================================

/**
 * Parameters for saving a payment method from Stripe
 */
export interface SavePaymentMethodFromStripeParams {
  /** User ID in our system */
  userId: number;
  /** Stripe Payment Method ID (pm_xxx) */
  extPaymentMethodId: string;
  /** Stripe Customer ID (cus_xxx) - optional if already attached */
  extPaymentUserId?: string;
  /** Auth token for API calls */
  authToken?: string | null;
}

/**
 * Save a payment method by having the backend fetch details from Stripe
 * 
 * This is the preferred method when using SetupIntent flow because:
 * 1. SetupIntent may not return full card details (brand, exp, etc.)
 * 2. Backend can reliably fetch all details from Stripe
 * 3. Single source of truth for payment method data
 * 
 * Backend should:
 * 1. Call stripe.paymentMethods.retrieve(extPaymentMethodId)
 * 2. Extract all relevant details (brand, last4, exp, fingerprint, etc.)
 * 3. Save to user_payment_method table
 * 4. Return the saved UserPaymentMethod
 */
export const savePaymentMethodFromStripe = async (
  params: SavePaymentMethodFromStripeParams
): Promise<UserPaymentMethod> => {
  const { userId, extPaymentMethodId, extPaymentUserId, authToken } = params;

  const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/user/${userId}/payment-method/from-stripe`;

  const requestBody = {
    extPaymentMethodId,
    ...(extPaymentUserId && { extPaymentUserId }),
  };

  console.log('[PaymentMethodService] Saving payment method from Stripe:', {
    url,
    userId,
    extPaymentMethodId,
    extPaymentUserId: extPaymentUserId || '(from existing attachment)',
  });

  const response = await axios.post(url, requestBody, {
    headers: {
      'Content-Type': 'application/json',
      ...(authToken && {
        Authorization: `Bearer ${authToken}`,
        'x-cog-id': authToken,
      }),
    },
  });

  console.log('[PaymentMethodService] Payment method saved from Stripe:', response.data);

  return response.data;
};

// ============================================================================
// CREATE AND CONFIRM PAYMENT INTENT - Capture payment with saved method
// ============================================================================

/**
 * Parameters for creating and confirming a PaymentIntent
 */
export interface CreateAndConfirmPaymentIntentParams {
  /** Amount in dollars (will be converted to cents) */
  amount: number;
  /** Stripe Payment Method ID (pm_xxx) */
  paymentMethodId: string;
  /** Stripe Customer ID (cus_xxx) */
  extPaymentUserId: string;
  /** Whether to confirm immediately (default: true) */
  confirm?: boolean;
  /** Auth token for API calls */
  authToken?: string | null;
}

/**
 * Result from creating/confirming a PaymentIntent
 */
export interface CreateAndConfirmPaymentIntentResult {
  success: boolean;
  message: string;
  paymentIntentId?: string;
  clientSecret?: string;
  status?: string;
}

/**
 * Create a PaymentIntent with a saved payment method and optionally confirm it
 * 
 * Flow:
 * 1. Create PaymentIntent with payment_method and customer
 * 2. If confirm=true, payment is captured immediately
 * 3. Returns PaymentIntent ID and status
 * 
 * Use this after SetupIntent flow to actually charge the customer.
 */
export const createAndConfirmPaymentIntent = async (
  params: CreateAndConfirmPaymentIntentParams
): Promise<CreateAndConfirmPaymentIntentResult> => {
  const { amount, paymentMethodId, extPaymentUserId, confirm = true, authToken } = params;

  try {
    console.log('[PaymentMethodService] Creating PaymentIntent with saved method...');
    console.log('[PaymentMethodService] Amount:', amount, 'PaymentMethod:', paymentMethodId, 'Customer:', extPaymentUserId, 'Confirm:', confirm);

    const url = `${getPaymentApiBaseUrl()}${getPaymentApiPathPrefix()}/payment-intent`;
    
    const requestBody = {
      amount: Math.round(amount * 100), // Convert to cents
      paymentMethodId,
      extPaymentUserId,
      confirm,
    };

    const response = await axios.post(url, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });

    const entity = response.data;
    if (!entity) {
      console.error('[PaymentMethodService] Error creating PaymentIntent: No response data');
      return {
        success: false,
        message: 'Failed to create payment intent.',
      };
    }

    const paymentIntentId = entity.id || entity.paymentIntentId;
    
    console.log('[PaymentMethodService] ✅ PaymentIntent created:', paymentIntentId);
    console.log('[PaymentMethodService] Status:', entity.status);

    return {
      success: true,
      message: confirm ? 'Payment captured successfully.' : 'Payment intent created.',
      paymentIntentId,
      clientSecret: entity.client_secret,
      status: entity.status,
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error creating PaymentIntent:', err);
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred creating payment intent.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

/**
 * Update program schedule with recurring payment method
 */
export const updateProgramSchedule = async (params: {
  programId: number;
  patientEncounterId: number;
  recurringUserPaymentMethodId: number;
  nextPaymentDueDt?: string;
  userEmail?: string;
  authToken?: string;
}): Promise<{ success: boolean; message: string }> => {
  const { programId, patientEncounterId, recurringUserPaymentMethodId, nextPaymentDueDt, userEmail, authToken } = params;
  
  try {
    const url = `${configSettings.unified_aws_app_gateway_uri}/payments/v3/program/${programId}`;
    const now = new Date().toISOString();
    
    const payload = {
      patientEncounterId,
      isPaidInFull: false,
      lastUpdateUserId: userEmail || 'system',
      lastUpdateDt: now,
      patientPaymentSchedule: {
        paymentFreq: 'M',
        recurringUserPaymentMethodId,
        scheduleStatus: 'active',
        lastUpdateUserId: recurringUserPaymentMethodId, // Using payment method id as a proxy for user id
        lastUpdateDt: now,
        ...(nextPaymentDueDt && { nextPaymentDueDt }),
      },
    };
    
    console.log('[PaymentMethodService] Updating program schedule:', { url, payload });
    
    await axios.patch(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken && {
          'Authorization': `Bearer ${authToken}`,
          'x-cog-id': authToken,
        }),
      },
    });
    
    console.log('[PaymentMethodService] ✅ Program schedule updated successfully');
    
    return {
      success: true,
      message: 'Schedule updated successfully.',
    };
  } catch (err: any) {
    console.error('[PaymentMethodService] Error updating program schedule:', err);
    const responseData = err.response?.data;
    const errorMessage = 
      responseData?.externalResponse?.message || 
      responseData?.message || 
      err.message || 
      'An unexpected error occurred updating program schedule.';
    return {
      success: false,
      message: errorMessage,
    };
  }
};
